import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, ClipboardPaste, Search, AlertTriangle, Layers, Paintbrush, ArrowLeftRight, Save, FileStack, LayoutDashboard, Warehouse, ListTree, Boxes, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";
import { useLiveRefresh } from "@/lib/live";
import SubstratesPanel from "@/components/inventory/SubstratesPanel";
import StoresDashboard from "@/components/stores/StoresDashboard";
import StockTab from "@/components/stores/StockTab";
import RollsTab from "@/components/stores/RollsTab";
import MovementsTab from "@/components/stores/MovementsTab";
import JobsTab from "@/components/stores/JobsTab";
import { RollsLink } from "@/components/stores/storesUi";

const ORANGE = "#FF5A00";
const inr = (n) => (n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const TABS = ["dashboard", "jobs", "stock", "rolls", "movements", "raw", "ink", "stores", "substrates"];

export default function Inventory() {
  // ?tab= deep links (dashboard tiles, quick links, roll / item / BPR links) land on the right view.
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "dashboard";
  const q = params.get("q") || "";
  // A new deep link while the tab is already open starts it afresh with the link's filters.
  const linkKey = params.toString();
  return (
    <div>
      <PageHeader subtitle="Stock · Purchases · Consumption" title="Inventory" subtitleKey="page.inventory.subtitle" titleKey="page.inventory.title" />
      <div className="p-8">
        <Tabs value={tab} onValueChange={(t) => setParams({ tab: t }, { replace: true })} className="w-full">
          <TabsList className="rounded-sm bg-white border border-neutral-200 h-auto flex-wrap justify-start">
            <TabsTrigger data-testid="inv-tab-dashboard" value="dashboard" className="rounded-sm">
              <LayoutDashboard size={13} className="mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-jobs" value="jobs" className="rounded-sm">
              <ClipboardList size={13} className="mr-1.5" /> Jobs
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-stock" value="stock" className="rounded-sm">
              <Warehouse size={13} className="mr-1.5" /> Stock
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-rolls" value="rolls" className="rounded-sm">
              <Boxes size={13} className="mr-1.5" /> Rolls
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-movements" value="movements" className="rounded-sm">
              <ListTree size={13} className="mr-1.5" /> Movements
            </TabsTrigger>
            <span className="mx-1 h-5 w-px bg-neutral-200" aria-hidden />
            <TabsTrigger data-testid="inv-tab-raw" value="raw" className="rounded-sm">
              <Layers size={13} className="mr-1.5" /> Raw Material
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-ink" value="ink" className="rounded-sm">
              <Paintbrush size={13} className="mr-1.5" /> Ink & Varnish
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-stores" value="stores" className="rounded-sm">
              <ArrowLeftRight size={13} className="mr-1.5" /> Stores I/O
            </TabsTrigger>
            <TabsTrigger data-testid="inv-tab-substrates" value="substrates" className="rounded-sm">
              <FileStack size={13} className="mr-1.5" /> Substrates
            </TabsTrigger>
          </TabsList>
          <TabsContent value="dashboard" className="mt-4"><StoresDashboard /></TabsContent>
          <TabsContent value="jobs" className="mt-4"><JobsTab /></TabsContent>
          <TabsContent value="stock" className="mt-4">
            <StockTab key={linkKey} initialView={params.get("view") || "report"} initialStatus={params.get("status") || "all"} initialQ={q} />
          </TabsContent>
          <TabsContent value="rolls" className="mt-4">
            <RollsTab key={linkKey} initialQc={params.get("qc") || "all"} initialQ={q} />
          </TabsContent>
          <TabsContent value="movements" className="mt-4">
            <MovementsTab key={linkKey} initialType={params.get("type") || "receipt"} initialBpr={params.get("bpr") || ""} initialQ={q}
                          initialFrom={params.get("from") || ""} initialTo={params.get("to") || ""} />
          </TabsContent>
          <TabsContent value="raw" className="mt-4"><MaterialsTab category="raw" /></TabsContent>
          <TabsContent value="ink" className="mt-4"><MaterialsTab category="ink" /></TabsContent>
          <TabsContent value="stores" className="mt-4"><StoresIOTab /></TabsContent>
          <TabsContent value="substrates" className="mt-4"><SubstratesPanel /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============================================================================
// MATERIALS TAB (used for both Raw Material & Ink)
// ============================================================================
function MaterialsTab({ category }) {
  const { user } = useAuth();
  const canEdit = hasRole(user, "production_manager", "accounts");
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState({ suppliers: [], paper_codes: [], widths: [], types: [], brands: [] });
  const [supplier, setSupplier] = useState("all");
  const [paperCode, setPaperCode] = useState("all");
  const [width, setWidth] = useState("all");
  const [type, setType] = useState("all");
  const [brand, setBrand] = useState("all");
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = async () => {
    const params = { category };
    if (supplier !== "all") params.supplier = supplier;
    if (paperCode !== "all") params.paper_code = paperCode;
    if (width !== "all") params.roll_width = Number(width);
    if (q) params.q = q;
    const { data } = await api.get("/inventory/materials", { params });
    let filtered = data;
    if (type !== "all") filtered = filtered.filter((m) => m.type === type);
    if (brand !== "all") filtered = filtered.filter((m) => m.brand === brand);
    setRows(filtered);
    const { data: fac } = await api.get("/inventory/materials/facets", { params: { category } });
    setFacets(fac);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, supplier, paperCode, width, type, brand]);
  const reload = useLiveRefresh("stores", load);

  // Rate is per square metre for roll stock (SQMTRS) — the ledger's own unit is metres, so converting through
  // the roll's width first is what backend/routes/stores.py's to_sqm() does for the Stock Report tab; matching
  // it here keeps this page's Value the same figure, not metres × a per-SQM rate (10x+ too high at most widths).
  const toSqm = (r) => ((r.uom || "").toUpperCase() === "SQMTRS" ? (r.current_stock || 0) * (r.roll_width || 0) / 1000 : (r.current_stock || 0));
  const totalValue = useMemo(
    () => rows.reduce((s, r) => s + toSqm(r) * (r.rate || r.unit_cost || 0), 0),
    [rows]
  );
  const lowCount = rows.filter((r) => (r.current_stock || 0) <= (r.min_stock_level ?? r.reorder_level ?? 0)).length;

  return (
    <div>
      {/* Filters + Actions */}
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        {category === "raw" && (
          <>
            <FilterSelect label="Material Type" value={type} onChange={setType} options={facets.types} testid={`${category}-filter-type`} />
            <FilterSelect label="Material Code" value={paperCode} onChange={setPaperCode} options={facets.paper_codes} testid={`${category}-filter-code`} />
            <FilterSelect label="Width (mm)" value={width} onChange={setWidth} options={facets.widths.map(String)} testid={`${category}-filter-width`} />
          </>
        )}
        {category === "ink" && (
          <FilterSelect label="Brand" value={brand} onChange={setBrand} options={facets.brands} testid={`${category}-filter-brand`} />
        )}
        <FilterSelect label="Supplier" value={supplier} onChange={setSupplier} options={facets.suppliers} testid={`${category}-filter-supplier`} />
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid={`${category}-search`} value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()} className="pl-8 rounded-sm"
              placeholder="Name / SKU / code / paper type…" />
          </div>
        </div>
        <Button data-testid={`${category}-refresh`} onClick={load} variant="outline" className="rounded-sm">Apply</Button>
        {canEdit && (
          <>
            <Button data-testid={`${category}-paste-open`} onClick={() => setPasteOpen(true)} variant="outline" className="rounded-sm gap-1.5">
              <ClipboardPaste size={13} /> Paste from Excel
            </Button>
            {/* A new raw material only gets created roll-tracked, through a Goods Receipt's "+ new item" —
                adding one here instead would leave it with no roll ledger, so Issue/Return can't use it. */}
            {category === "raw" ? (
              <Link to="/inventory?tab=movements&type=receipt" className="text-xs text-[#FF5A00] hover:underline self-center" data-testid="raw-add-redirect">
                New raw material? Add it via Goods Receipt →
              </Link>
            ) : (
              <Button data-testid={`${category}-add`} onClick={() => setAddOpen(true)} className="rounded-sm gap-1.5 bg-[#FF5A00] hover:bg-[#e04f00]">
                <Plus size={13} /> Add
              </Button>
            )}
          </>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label={category === "ink" ? "Ink SKUs" : "Materials"} value={inr(rows.length)} />
        <Kpi label="Total stock value" value={`₹${inr(totalValue)}`} />
        <Kpi label="Low-stock items" value={inr(lowCount)} tone={lowCount > 0 ? "danger" : "ok"} />
        <Kpi label={`Category`} value={category === "ink" ? "Ink & Varnish" : "Raw Material"} />
      </div>

      {/* Table */}
      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${category}-table`}>
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5">SKU</th>
              <th className="text-left px-3 py-2.5">Name</th>
              {category === "raw" ? (
                <>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Paper Type</th>
                  <th className="text-left px-3 py-2.5">Paper Code</th>
                  <th className="text-right px-3 py-2.5">GSM</th>
                  <th className="text-right px-3 py-2.5">Width</th>
                  <th className="text-left px-3 py-2.5">Roll Details</th>
                </>
              ) : (
                <>
                  <th className="text-left px-3 py-2.5">Brand</th>
                  <th className="text-left px-3 py-2.5">Colour / Type</th>
                </>
              )}
              <th className="text-left px-3 py-2.5">Supplier</th>
              <th className="text-right px-3 py-2.5">Stock</th>
              <th className="text-left px-3 py-2.5">UOM</th>
              <th className="text-right px-3 py-2.5">Rate</th>
              <th className="text-right px-3 py-2.5">Value</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={category === "raw" ? 14 : 10} className="px-4 py-12 text-center text-neutral-400 text-sm">
                No items. Click <b>Paste from Excel</b> to bulk-import or <b>Add</b> to create one.
              </td></tr>
            )}
            {rows.map((r) => {
              const value = toSqm(r) * (r.rate || r.unit_cost || 0);
              const low = (r.current_stock || 0) <= (r.min_stock_level ?? r.reorder_level ?? 0);
              return (
                <tr key={r.id} className="border-t border-neutral-100 data-row" data-testid={`${category}-row-${r.sku}`}>
                  <td className="px-3 py-2 font-mono-num text-xs">
                    {r.stock_source === "ledger" ? <RollsLink q={r.sku} /> : r.sku}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    {r.name}
                    {low && <AlertTriangle size={12} className="inline ml-1 text-rose-600" />}
                  </td>
                  {category === "raw" ? (
                    <>
                      <td className="px-3 py-2 text-xs">{r.type || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.paper_type || "—"}</td>
                      <td className="px-3 py-2 font-mono-num text-xs">{r.paper_code || "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{r.gsm ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono-num">{r.roll_width ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-neutral-600 whitespace-pre-line max-w-xs">{r.roll_details || "—"}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 text-xs">{r.brand || "—"}</td>
                      <td className="px-3 py-2 text-xs">{r.color_type || r.ink_name || "—"}</td>
                    </>
                  )}
                  <td className="px-3 py-2 text-xs">{r.supplier || "—"}</td>
                  <td className={`px-3 py-2 text-right font-mono-num font-semibold ${low ? "text-rose-700" : ""}`}>{inr(r.current_stock)}</td>
                  <td className="px-3 py-2 text-xs">{r.unit || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-neutral-600">{inr(r.rate || r.unit_cost || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">₹{inr(value)}</td>
                  <td className="px-3 py-2">
                    {canEdit && (
                      <button data-testid={`${category}-edit-${r.sku}`} onClick={() => setEditRow(r)}
                        className="text-xs text-[#FF5A00] hover:underline">Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(addOpen || editRow) && (
        <MaterialFormDialog
          open={addOpen || !!editRow}
          onClose={() => { setAddOpen(false); setEditRow(null); }}
          category={category}
          material={editRow}
          onSaved={() => { setAddOpen(false); setEditRow(null); reload(); }}
        />
      )}
      <PasteMaterialsDialog
        open={pasteOpen} onClose={() => setPasteOpen(false)}
        category={category} onDone={() => { setPasteOpen(false); reload(); }}
      />
    </div>
  );
}

// ============================================================================
// Add / Edit Material Dialog
// ============================================================================
function MaterialFormDialog({ open, onClose, category, material, onSaved }) {
  // Stores items take their stock from the rolls; code + width are their item code (backend/routes/inventory.py).
  const ledger = material?.stock_source === "ledger";
  const [f, setF] = useState(material || {
    sku: "", name: "", category, unit: category === "ink" ? "kg" : "mtrs",
    current_stock: 0, rate: 0, supplier: "",
  });
  useEffect(() => { setF(material || { sku: "", name: "", category, unit: category === "ink" ? "kg" : "mtrs", current_stock: 0, rate: 0 }); }, [material, category]);

  const save = async () => {
    try {
      const payload = { ...f, category };
      // number coerce
      ["current_stock", "rate", "unit_cost", "gsm", "roll_width", "no_of_rolls",
        "min_stock_level", "max_stock_level", "pack_size", "opening_stock"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = Number(payload[k]);
      });
      payload.unit_cost = payload.unit_cost || payload.rate || 0;
      if (material) {
        await api.patch(`/inventory/materials/${material.id}`, payload);
      } else {
        await api.post("/inventory/materials", payload);
      }
      toast.success(material ? "Material updated" : "Material created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{material ? "Edit" : "Add"} {category === "ink" ? "Ink / Varnish" : "Raw Material"}</DialogTitle>
          <DialogDescription>{category === "ink" ? "For inks, varnishes, primers." : "For paper, film, and adhesive substrates."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="SKU *" value={f.sku} onChange={(v) => setF({ ...f, sku: v })} testid="mat-sku" disabled={!!material} />
          <Field label="Name *" value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="mat-name" />
          <Field label="Supplier" value={f.supplier} onChange={(v) => setF({ ...f, supplier: v })} testid="mat-supplier" />
          {category === "raw" ? (
            <>
              <Field label="Type" value={f.type} onChange={(v) => setF({ ...f, type: v })} testid="mat-type" placeholder="Self Adhesive Chromo Paper" />
              <Field label="Paper Type" value={f.paper_type} onChange={(v) => setF({ ...f, paper_type: v })} testid="mat-paper-type" />
              <Field label="Paper Code" value={f.paper_code} onChange={(v) => setF({ ...f, paper_code: v })} testid="mat-paper-code" disabled={ledger} />
              <Field label="GSM" type="number" value={f.gsm} onChange={(v) => setF({ ...f, gsm: v })} testid="mat-gsm" />
              <Field label="Width (mm)" type="number" value={f.roll_width} onChange={(v) => setF({ ...f, roll_width: v })} testid="mat-width" disabled={ledger} />
              <Field label="No. of Rolls" type="number" value={f.no_of_rolls} onChange={(v) => setF({ ...f, no_of_rolls: v })} testid="mat-rolls" />
              <div className="col-span-3">
                <label className="label-caps block mb-1 text-[10px]">Roll Details (free text — one roll per line)</label>
                <textarea data-testid="mat-roll-details" value={f.roll_details || ""}
                  onChange={(e) => setF({ ...f, roll_details: e.target.value })}
                  className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm font-mono-num h-20"
                  placeholder={"2000*22-300mm\n2100*4-300mm"} />
              </div>
            </>
          ) : (
            <>
              <Field label="Brand" value={f.brand} onChange={(v) => setF({ ...f, brand: v })} testid="mat-brand" placeholder="Seigwerk" />
              <Field label="Colour / Type" value={f.color_type} onChange={(v) => setF({ ...f, color_type: v })} testid="mat-color" placeholder="Cyan / Pantone 185 C" />
              <Field label="Ink Name" value={f.ink_name} onChange={(v) => setF({ ...f, ink_name: v })} testid="mat-ink-name" />
              <Field label="Pack Size" type="number" value={f.pack_size} onChange={(v) => setF({ ...f, pack_size: v })} testid="mat-pack-size" />
            </>
          )}
          <Field label="UOM" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} testid="mat-uom" placeholder={category === "ink" ? "kg" : "mtrs"} />
          <Field label="Current Stock" type="number" value={f.current_stock} onChange={(v) => setF({ ...f, current_stock: v })} testid="mat-stock" disabled={ledger} />
          <Field label="Rate (₹)" type="number" value={f.rate} onChange={(v) => setF({ ...f, rate: v })} testid="mat-rate" />
          <Field label="Min Stock Level" type="number" value={f.min_stock_level} onChange={(v) => setF({ ...f, min_stock_level: v })} testid="mat-min" />
          <Field label="Max Stock Level" type="number" value={f.max_stock_level} onChange={(v) => setF({ ...f, max_stock_level: v })} testid="mat-max" />
        </div>
        {ledger && (
          <p className="text-xs text-neutral-500" data-testid="mat-ledger-note">
            Stores item: stock follows its rolls. Record receipts, issues and returns under Inventory → Movements.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="mat-save" onClick={save} className="bg-[#FF5A00] hover:bg-[#e04f00]"><Save size={13} className="mr-1" /> Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Paste Materials Dialog
// ============================================================================
function PasteMaterialsDialog({ open, onClose, category, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const columns = category === "ink"
    ? "Supplier · Brand · Colour · Code · Opening · Inward · Outward · Closing · UOM · Rate"
    : "Type · Supplier · Roll Details · Paper Type · Paper Code · GSM · Value · Total Sqm Purchase · Total Sqm Closing · Rate · Size (width mm) · UOM · Opening Stock · No of Rolls";
  const example = category === "ink"
    ? "Inctech\tSeigwerk\tCyan\tINK-CY-01\t5\t20\t3\t22\tkg\t550"
    : "Self Adhesive\tAvery Dennison\t2000*22-300mm\tFASSON CHROMO ART\tAM118451\t80\t165344\t5923\t19744\t28\t300\tSQM\t10000\t22";

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste some rows first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/materials/paste", { pasted_text: text, category });
      toast.success(`${data.created} created · ${data.updated} updated${data.errors?.length ? ` · ${data.errors.length} errors` : ""}`);
      setText(""); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Paste {category === "ink" ? "Ink & Varnish" : "Raw Material"} from Excel</DialogTitle>
          <DialogDescription>Copy rows from Excel and paste. Header row auto-detected.</DialogDescription>
        </DialogHeader>
        <div className="border border-neutral-200 bg-neutral-50 p-2 text-[11px] font-mono-num mb-2">Columns: {columns}</div>
        <textarea data-testid="mat-paste-textarea" value={text} onChange={(e) => setText(e.target.value)}
          className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm font-mono-num h-56"
          placeholder={example} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="mat-paste-submit" onClick={submit} disabled={busy} className="bg-[#FF5A00] hover:bg-[#e04f00]">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Stores I/O Tab
// ============================================================================
function StoresIOTab() {
  const { user } = useAuth();
  const canEdit = hasRole(user, "production_manager", "accounts");
  const [rows, setRows] = useState([]);
  const [dept, setDept] = useState("all");
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (dept !== "all") params.dept = dept;
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/inventory/stores-io", { params });
    let filtered = data;
    if (q.trim()) {
      const n = q.toLowerCase();
      filtered = filtered.filter((r) => (r.material_name || "").toLowerCase().includes(n) || (r.material_code || "").toLowerCase().includes(n));
    }
    setRows(filtered);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept, from, to]);
  const reload = useLiveRefresh("stores", load);

  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="label-caps block mb-1">Department</label>
          <Select value={dept} onValueChange={setDept}>
            <SelectTrigger data-testid="stores-dept" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Depts</SelectItem>
              <SelectItem value="PRINTING">Printing</SelectItem>
              <SelectItem value="SLITTING">Slitting</SelectItem>
              <SelectItem value="PACKING">Packing</SelectItem>
              <SelectItem value="INSPECTION">Inspection</SelectItem>
              <SelectItem value="RETURN">Returns</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><label className="label-caps block mb-1">From</label><Input data-testid="stores-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm w-40" /></div>
        <div><label className="label-caps block mb-1">To</label><Input data-testid="stores-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm w-40" /></div>
        <div className="flex-1 min-w-56">
          <label className="label-caps block mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="stores-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-8 rounded-sm" placeholder="material / code" />
          </div>
        </div>
        <Button data-testid="stores-refresh" onClick={load} variant="outline" className="rounded-sm">Apply</Button>
        {canEdit && (
          <>
            <Button data-testid="stores-paste-open" onClick={() => setPasteOpen(true)} variant="outline" className="rounded-sm gap-1.5"><ClipboardPaste size={13} /> Paste Excel</Button>
            <Button data-testid="stores-add" onClick={() => setAddOpen(true)} className="rounded-sm gap-1.5 bg-[#FF5A00] hover:bg-[#e04f00]"><Plus size={13} /> Add Txn</Button>
          </>
        )}
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="stores-table">
          <thead className="bg-neutral-50 label-caps text-[10px]">
            <tr>
              <th className="text-left px-3 py-2.5">Date</th>
              <th className="text-left px-3 py-2.5">Material Name</th>
              <th className="text-left px-3 py-2.5">Code</th>
              <th className="text-right px-3 py-2.5">Width</th>
              <th className="text-right px-3 py-2.5">Length</th>
              <th className="text-right px-3 py-2.5">Rolls</th>
              <th className="text-right px-3 py-2.5">Qty</th>
              <th className="text-left px-3 py-2.5">UOM</th>
              <th className="text-left px-3 py-2.5">Dept</th>
              <th className="text-right px-3 py-2.5">Return</th>
              <th className="text-right px-3 py-2.5">Consumed</th>
              <th className="text-left px-3 py-2.5">Direction</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={12} className="px-4 py-12 text-center text-neutral-400">No transactions.</td></tr>}
            {rows.slice(0, 800).map((r) => (
              <tr key={r.id} className="border-t border-neutral-100 data-row" data-testid={`stores-row-${r.id}`}>
                <td className="px-3 py-2 font-mono-num text-xs text-neutral-600">{(r.date || "").slice(0, 10)}</td>
                <td className="px-3 py-2 font-medium">{r.material_name}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{r.material_code || "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{r.width ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{r.length ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num">{r.rolls ?? "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold">{inr(r.qty)}</td>
                <td className="px-3 py-2 text-xs">{r.uom}</td>
                <td className="px-3 py-2 text-xs">
                  {r.dept_to && (
                    <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest bg-blue-50 text-blue-800 border border-blue-200">{r.dept_to}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-emerald-700">{r.return_qty != null ? inr(r.return_qty) : "—"}</td>
                <td className="px-3 py-2 text-right font-mono-num text-amber-700">{r.consumed_qty != null ? inr(r.consumed_qty) : "—"}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-widest ${r.direction === "inward" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{r.direction || "outward"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StoresIOAddDialog open={addOpen} onClose={() => setAddOpen(false)} onDone={() => { setAddOpen(false); reload(); }} />
      <PasteStoresDialog open={pasteOpen} onClose={() => setPasteOpen(false)} onDone={() => { setPasteOpen(false); reload(); }} />
    </div>
  );
}

function StoresIOAddDialog({ open, onClose, onDone }) {
  const [materials, setMaterials] = useState([]);
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), direction: "outward", uom: "MTRS", dept_to: "PRINTING", qty: 0 });

  useEffect(() => { if (open) api.get("/inventory/materials").then(({ data }) => setMaterials(data)); }, [open]);
  const save = async () => {
    try {
      const payload = { ...f, qty: Number(f.qty), width: Number(f.width) || null, length: Number(f.length) || null,
        rolls: Number(f.rolls) || null, return_qty: f.return_qty === "" || f.return_qty == null ? null : Number(f.return_qty),
        consumed_qty: f.consumed_qty === "" || f.consumed_qty == null ? null : Number(f.consumed_qty) };
      await api.post("/inventory/stores-io", payload);
      toast.success("Transaction recorded");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Stores Transaction</DialogTitle><DialogDescription>Inward / outward with auto stock adjustment.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Date" type="date" value={f.date} onChange={(v) => setF({ ...f, date: v })} testid="sio-date" />
          <div>
            <label className="label-caps block mb-1 text-[10px]">Direction</label>
            <Select value={f.direction} onValueChange={(v) => setF({ ...f, direction: v })}>
              <SelectTrigger data-testid="sio-direction" className="rounded-sm h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outward">Outward (issue)</SelectItem>
                <SelectItem value="inward">Inward (receipt)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-caps block mb-1 text-[10px]">Material</label>
            <Select value={f.material_id || ""} onValueChange={(v) => {
              const m = materials.find((x) => x.id === v);
              setF({ ...f, material_id: v, material_name: m?.name, material_code: m?.paper_code || m?.sku, uom: m?.unit });
            }}>
              <SelectTrigger data-testid="sio-material" className="rounded-sm h-8"><SelectValue placeholder="Pick material" /></SelectTrigger>
              <SelectContent className="max-h-80">
                {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.sku}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Field label="Width" type="number" value={f.width} onChange={(v) => setF({ ...f, width: v })} testid="sio-width" />
          <Field label="Length" type="number" value={f.length} onChange={(v) => setF({ ...f, length: v })} testid="sio-length" />
          <Field label="Rolls" type="number" value={f.rolls} onChange={(v) => setF({ ...f, rolls: v })} testid="sio-rolls" />
          <Field label="Qty *" type="number" value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="sio-qty" />
          <Field label="UOM" value={f.uom} onChange={(v) => setF({ ...f, uom: v })} testid="sio-uom" />
          <div>
            <label className="label-caps block mb-1 text-[10px]">Dept To</label>
            <Select value={f.dept_to} onValueChange={(v) => setF({ ...f, dept_to: v })}>
              <SelectTrigger data-testid="sio-dept" className="rounded-sm h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PRINTING">Printing</SelectItem>
                <SelectItem value="SLITTING">Slitting</SelectItem>
                <SelectItem value="PACKING">Packing</SelectItem>
                <SelectItem value="INSPECTION">Inspection</SelectItem>
                <SelectItem value="RETURN">Return</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field label="Return Qty" type="number" value={f.return_qty} onChange={(v) => setF({ ...f, return_qty: v })} testid="sio-return" />
          <Field label="Consumed Qty" type="number" value={f.consumed_qty} onChange={(v) => setF({ ...f, consumed_qty: v })} testid="sio-consumed" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="sio-save" onClick={save} className="bg-[#FF5A00] hover:bg-[#e04f00]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasteStoresDialog({ open, onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return toast.error("Paste rows first");
    setBusy(true);
    try {
      const { data } = await api.post("/inventory/stores-io/paste", { pasted_text: text, category: "stores_io" });
      toast.success(`${data.created} rows imported`);
      setText(""); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Paste Stores I/O from Excel</DialogTitle>
          <DialogDescription>Bulk import stores transactions. Historical data — stock NOT auto-adjusted.</DialogDescription>
        </DialogHeader>
        <div className="border border-neutral-200 bg-neutral-50 p-2 text-[11px] font-mono-num mb-2">
          Columns: Date · Material Name · Material Code · Width · Width UOM · Length · Length UOM · Rolls · Qty · Qty UOM · Dept To · Return · Consumed
        </div>
        <textarea data-testid="stores-paste-textarea" value={text} onChange={(e) => setText(e.target.value)}
          className="w-full border border-neutral-200 rounded-sm px-3 py-2 text-sm font-mono-num h-56"
          placeholder={"2026-07-01\tFASSON CHROMO ART\tAM118451\t300\tmm\t2000\tm\t1\t500\tMTRS\tPRINTING\t\t500"} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="stores-paste-submit" onClick={submit} disabled={busy} className="bg-[#FF5A00] hover:bg-[#e04f00]">{busy ? "Importing…" : "Import"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function Field({ label, value, onChange, testid, type = "text", placeholder, disabled }) {
  return (
    <div>
      <label className="label-caps block mb-1 text-[10px]">{label}</label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled} className="rounded-sm h-8 text-sm" />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-caps block mb-1">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="rounded-sm w-40"><SelectValue /></SelectTrigger>
        <SelectContent className="max-h-80">
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={String(o)}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const cls = tone === "danger" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : "text-neutral-900";
  return (
    <div className="border border-neutral-200 bg-white px-4 py-3">
      <div className="label-caps mb-1 text-[10px]">{label}</div>
      <div className={`font-mono-num text-2xl font-bold ${cls}`}>{value}</div>
    </div>
  );
}
